/**
 * ====================================================================
 * FIKO CAD ORDINATE DIMENSION
 * ====================================================================
 *
 * Koordinat ölçüsü - Datum'dan X, Y veya Z koordinat değerini gösterir.
 * CATIA/AutoCAD'in "Ordinate Dimension" özelliğine karşılık gelir.
 *
 * Özellikler:
 * - Tek eksen koordinat gösterimi (X, Y veya Z)
 * - Bükülmüş leader line (jog)
 * - Datum origin referansı
 * - Otomatik pozitif/negatif işareti
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    BaseDimensionOptions,
    AxisLock,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// ORDINATE DIMENSION OPTIONS
// ====================================================================

/**
 * Hangi eksen koordinatı gösterilecek
 */
export enum OrdinateAxis {
    X = 'X',
    Y = 'Y',
    Z = 'Z'
}

/**
 * Ordinate dimension için özel seçenekler
 */
export interface OrdinateDimensionOptions extends BaseDimensionOptions {
    /** Hangi eksen koordinatı */
    ordinateAxis?: OrdinateAxis;
    /** Leader line uzunluğu */
    leaderLength?: number;
    /** Jog (bükülme) açısı */
    jogAngle?: number;
    /** Jog uzunluğu */
    jogLength?: number;
    /** İşaret göster (+/-) */
    showSign?: boolean;
    /** Eksen etiketi göster (X=, Y=, Z=) */
    showAxisLabel?: boolean;
}

/**
 * Ordinate bilgisi
 */
export interface OrdinateInfo {
    point: THREE.Vector3;
    axis: OrdinateAxis;
    value: number;
    isNegative: boolean;
}

// ====================================================================
// ORDINATE DIMENSION CLASS
// ====================================================================

/**
 * Ordinate Dimension - Koordinat ölçüsü
 */
export class OrdinateDimension extends Dimension {
    // Datum (origin) noktası
    private datumPoint: THREE.Vector3;

    // Ölçülecek nokta
    private targetPoint: THREE.Vector3;

    // Ordinate bilgisi
    private ordinateInfo: OrdinateInfo;

    // Çizim elemanları
    private leaderLine: LineSegments2 | null = null;
    private jogLine: LineSegments2 | null = null;
    private extensionLine: LineSegments2 | null = null;
    private label: THREE.Sprite | null = null;

    // Pre-allocated
    private readonly _tempVec = new THREE.Vector3();

    // Sahne referansı
    private scene: FikoSahne;

    // SABİT BOYUT
    sabitBoyut = true;
    private hazirlaListenerEklendi = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // PERFORMANS
    private lastKameraUpdateTime = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS = 100;
    private targetScaleAdjustment = 1;
    private currentScaleAdjustment = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        datumPoint: THREE.Vector3,
        targetPoint: THREE.Vector3,
        options: OrdinateDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.ORDINATE, options);

        this.datumPoint = datumPoint.clone();
        this.targetPoint = targetPoint.clone();
        this.scene = scene;

        // Default ordinate info
        this.ordinateInfo = {
            point: this.targetPoint.clone(),
            axis: options.ordinateAxis || OrdinateAxis.X,
            value: 0,
            isNegative: false
        };
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Ordinate değerini hesapla
            this.calculateOrdinate();

            // Toplam değer
            this._value = this.calculateValue();

            // Validate - ordinate 0 olabilir, bu geçerli
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Geçersiz koordinat'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Ordinate dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Ordinate değerini hesapla
     */
    private calculateOrdinate(): void {
        const options = this._options as OrdinateDimensionOptions;
        const axis = options.ordinateAxis || OrdinateAxis.X;

        let value: number;

        switch (axis) {
            case OrdinateAxis.X:
                value = this.targetPoint.x - this.datumPoint.x;
                break;
            case OrdinateAxis.Y:
                value = this.targetPoint.y - this.datumPoint.y;
                break;
            case OrdinateAxis.Z:
                value = this.targetPoint.z - this.datumPoint.z;
                break;
            default:
                value = 0;
        }

        this.ordinateInfo = {
            point: this.targetPoint.clone(),
            axis,
            value: Math.abs(value),
            isNegative: value < 0
        };
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * Sahneye hazırlama
     */
    hazirla(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        try {
            if (this.label?.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Sessizce devam et
        }
    }

    /**
     * Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) return;

        if (now - this.lastKameraUpdateTime < OrdinateDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, OrdinateDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * Kamera değişikliğinde scale güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        if (this.sabitBoyut && this.label?.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * Smooth scale interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label?.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return !isNaN(this.ordinateInfo.value);
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        return this.ordinateInfo.value;
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as OrdinateDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const leaderLength = options.leaderLength || 10;
        const jogAngle = (options.jogAngle || 90) * Math.PI / 180;
        const jogLength = options.jogLength || 3;

        const axis = options.ordinateAxis || OrdinateAxis.X;

        // Leader yönü (eksene dik)
        let leaderDir: THREE.Vector3;
        let extensionDir: THREE.Vector3;

        switch (axis) {
            case OrdinateAxis.X:
                leaderDir = new THREE.Vector3(0, 1, 0);
                extensionDir = new THREE.Vector3(1, 0, 0);
                break;
            case OrdinateAxis.Y:
                leaderDir = new THREE.Vector3(1, 0, 0);
                extensionDir = new THREE.Vector3(0, 1, 0);
                break;
            case OrdinateAxis.Z:
                leaderDir = new THREE.Vector3(1, 0, 0);
                extensionDir = new THREE.Vector3(0, 0, 1);
                break;
            default:
                leaderDir = new THREE.Vector3(0, 1, 0);
                extensionDir = new THREE.Vector3(1, 0, 0);
        }

        // Kameraya göre ayarla (negatif değerlerde ters yön)
        if (this.ordinateInfo.isNegative) {
            leaderDir.negate();
        }

        // Extension line (noktadan leader başlangıcına)
        const extensionStart = this.targetPoint.clone();
        const extensionEnd = this.targetPoint.clone()
            .addScaledVector(leaderDir, 2); // Küçük gap

        this.extensionLine = this.createLine(
            [
                extensionStart.x, extensionStart.y, extensionStart.z,
                extensionEnd.x, extensionEnd.y, extensionEnd.z
            ],
            color, lineWidth * 0.7, 0.5
        );
        this.extensionLine.name = 'ExtensionLine';
        this.add(this.extensionLine);

        // Jog point (bükülme noktası)
        const jogPoint = extensionEnd.clone()
            .addScaledVector(leaderDir, jogLength);

        // Jog line
        this.jogLine = this.createLine(
            [
                extensionEnd.x, extensionEnd.y, extensionEnd.z,
                jogPoint.x, jogPoint.y, jogPoint.z
            ],
            color, lineWidth
        );
        this.jogLine.name = 'JogLine';
        this.add(this.jogLine);

        // Leader line (jog'dan label'a)
        // Yatay veya dikey çıkış
        let leaderEnd: THREE.Vector3;

        if (axis === OrdinateAxis.Y || axis === OrdinateAxis.Z) {
            // X yönünde çıkış
            leaderEnd = jogPoint.clone().addScaledVector(new THREE.Vector3(1, 0, 0), leaderLength);
        } else {
            // Y yönünde çıkış
            leaderEnd = jogPoint.clone().addScaledVector(new THREE.Vector3(0, 1, 0), leaderLength);
        }

        this.leaderLine = this.createLine(
            [
                jogPoint.x, jogPoint.y, jogPoint.z,
                leaderEnd.x, leaderEnd.y, leaderEnd.z
            ],
            color, lineWidth
        );
        this.leaderLine.name = 'LeaderLine';
        this.add(this.leaderLine);

        // Label
        const labelText = this.formatOrdinateValue(options);
        this.label = this.createLabel(labelText, leaderEnd, options);
        this.label.name = 'OrdinateLabel';
        this.add(this.label);
    }

    /**
     * Ordinate değerini formatla
     */
    private formatOrdinateValue(options: OrdinateDimensionOptions): string {
        const precision = options.precision || DIMENSION_CONSTANTS.DEFAULT_PRECISION;
        const unit = options.unit || 'mm';
        const showUnit = options.showUnit !== false;
        const showSign = options.showSign !== false;
        const showAxisLabel = options.showAxisLabel !== false;

        let text = '';

        // Eksen etiketi
        if (showAxisLabel) {
            text += `${this.ordinateInfo.axis}=`;
        }

        // İşaret
        if (showSign && this.ordinateInfo.isNegative) {
            text += '-';
        }

        // Değer
        text += this.ordinateInfo.value.toFixed(precision);

        // Birim
        if (showUnit) {
            text += ` ${unit}`;
        }

        return text;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3, options: OrdinateDimensionOptions): THREE.Sprite {
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;

        // Eksen renkleri
        let textColor: string;
        switch (this.ordinateInfo.axis) {
            case OrdinateAxis.X:
                textColor = '#E53935'; // Kırmızı
                break;
            case OrdinateAxis.Y:
                textColor = '#43A047'; // Yeşil
                break;
            case OrdinateAxis.Z:
                textColor = '#1E88E5'; // Mavi
                break;
            default:
                textColor = options.textColor || '#000000';
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 16;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Arka plan
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 5);
        ctx.fill();

        // Kenarlık
        ctx.strokeStyle = textColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Metin
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = textColor;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.012, canvas.height * 0.012, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        if (this.leaderLine) {
            (this.leaderLine.material as LineMaterial).color.setHex(color);
        }

        if (this.jogLine) {
            (this.jogLine.material as LineMaterial).color.setHex(color);
        }

        if (this.extensionLine) {
            (this.extensionLine.material as LineMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        const disposeLine = (line: LineSegments2 | null) => {
            if (line) {
                this.remove(line);
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            }
        };

        disposeLine(this.leaderLine);
        disposeLine(this.jogLine);
        disposeLine(this.extensionLine);

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        this.leaderLine = null;
        this.jogLine = null;
        this.extensionLine = null;
        this.label = null;
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Ordinate bilgisini al
     */
    getOrdinateInfo(): OrdinateInfo {
        return {
            ...this.ordinateInfo,
            point: this.ordinateInfo.point.clone()
        };
    }

    /**
     * Hedef noktayı değiştir
     */
    setTargetPoint(point: THREE.Vector3): void {
        this.targetPoint.copy(point);
        this.calculateOrdinate();
        this._value = this.calculateValue();
        this.buildGeometry();
    }

    /**
     * Datum noktasını değiştir
     */
    setDatumPoint(point: THREE.Vector3): void {
        this.datumPoint.copy(point);
        this.calculateOrdinate();
        this._value = this.calculateValue();
        this.buildGeometry();
    }

    /**
     * Ekseni değiştir
     */
    setAxis(axis: OrdinateAxis): void {
        (this._options as OrdinateDimensionOptions).ordinateAxis = axis;
        this.calculateOrdinate();
        this._value = this.calculateValue();
        this.buildGeometry();
    }

    /**
     * Koordinat değerini al
     */
    getCoordinateValue(): number {
        return this.ordinateInfo.isNegative ? -this.ordinateInfo.value : this.ordinateInfo.value;
    }

    /**
     * Dispose
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
